import { useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useAdminUser } from '../AdminUserContext';
import { api } from '../../api';

const LANG_LIST = [
  { code: 'en', label: 'English' }, { code: 'fr', label: 'Français' }, { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' }, { code: 'ru', label: 'Русский' }, { code: 'zh', label: '中文' },
  { code: 'tr', label: 'Türkçe' }, { code: 'id', label: 'Indonesia' }, { code: 'ms', label: 'Melayu' },
  { code: 'ur', label: 'اردو' }, { code: 'fa', label: 'فارسی' }, { code: 'hi', label: 'हिन्दी' },
  { code: 'bn', label: 'বাংলা' }, { code: 'pt', label: 'Português' }, { code: 'it', label: 'Italiano' },
  { code: 'sw', label: 'Kiswahili' }, { code: 'ha', label: 'Hausa' }, { code: 'yo', label: 'Yorùbá' },
  { code: 'am', label: 'አማርኛ' }, { code: 'so', label: 'Soomaali' }, { code: 'ta', label: 'தமிழ்' },
  { code: 'ml', label: 'മലയാളം' }, { code: 'ne', label: 'नेपाली' }, { code: 'si', label: 'සිංහල' },
  { code: 'ps', label: 'پښتو' }, { code: 'uz', label: 'Oʻzbekcha' }, { code: 'tg', label: 'Тоҷикӣ' },
  { code: 'bs', label: 'Bosanski' }, { code: 'sv', label: 'Svenska' }, { code: 'lt', label: 'Lietuvių' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' }, { code: 'fil', label: 'Filipino' }, { code: 'ti', label: 'ትግርኛ' },
  { code: 'om', label: 'Oromoo' }, { code: 'ug', label: 'ئۇيغۇرچە' },
];

const EMPTY_FORM = { username: '', password: '', role: 'operator', allowedLangs: [] };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', ok: true });

  const load = () => api.getAdminUsers().then(setUsers).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: '', ok: true }), 3500); };

  const startEdit = (u) => {
    setEditing(u.id);
    setForm({ username: u.username, password: '', role: u.role, allowedLangs: u.allowedLangs ? u.allowedLangs.split(',').filter(Boolean) : [] });
  };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const toggleLang = (code) => {
    setForm(f => ({
      ...f,
      allowedLangs: f.allowedLangs.includes(code) ? f.allowedLangs.filter(c => c !== code) : [...f.allowedLangs, code],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (editing) {
        await api.updateAdminUser(editing, payload);
        flash('User updated.');
      } else {
        await api.createAdminUser(payload);
        flash('User created.');
      }
      cancelEdit();
      load();
    } catch (err) {
      flash(err.message, false);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.deleteAdminUser(id); load(); flash('User deleted.'); }
    catch (err) { flash(err.message, false); }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1>Admin Users</h1>
        <p>Manage operator accounts and their language access</p>
      </div>

      {msg.text && (
        <div style={{ background: msg.ok ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)', border: `1px solid ${msg.ok ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)'}`, borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14, color: msg.ok ? '#22c55e' : '#ef4444' }}>
          {msg.text}
        </div>
      )}

      <div className="a-card" style={{ marginBottom: 24 }}>
        <h3><i className="fas fa-user-plus" style={{ marginInlineEnd: 8 }} />{editing ? 'Edit User' : 'Add User'}</h3>
        <form onSubmit={save}>
          <div className="a-form-grid" style={{ marginBottom: 16 }}>
            <div className="a-field-sm">
              <label>Username</label>
              <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="username" required={!editing} />
            </div>
            <div className="a-field-sm">
              <label>{editing ? 'New Password (leave blank to keep)' : 'Password'}</label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" required={!editing} />
            </div>
            <div className="a-field-sm">
              <label>Role</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ background: 'var(--a-surface2)', color: 'var(--a-text)', border: '1px solid var(--a-border)', borderRadius: 6, padding: '8px 12px', width: '100%' }}>
                <option value="admin">Admin (full access)</option>
                <option value="operator">Operator (limited)</option>
              </select>
            </div>
          </div>

          {form.role === 'operator' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--a-muted)' }}>
                Allowed Languages <span style={{ fontWeight: 400 }}>({form.allowedLangs.length} selected — empty means all)</span>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {LANG_LIST.map(({ code, label }) => {
                  const active = form.allowedLangs.includes(code);
                  return (
                    <button key={code} type="button" onClick={() => toggleLang(code)}
                      style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, border: `1px solid ${active ? 'var(--a-gold)' : 'var(--a-border)'}`, background: active ? 'rgba(212,170,106,.15)' : 'var(--a-surface2)', color: active ? 'var(--a-gold)' : 'var(--a-muted)', cursor: 'pointer' }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="a-btn-primary" type="submit" disabled={saving} style={{ width: 'auto', padding: '10px 28px' }}>
              {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
            </button>
            {editing && <button type="button" className="a-icon-btn" onClick={cancelEdit}>Cancel</button>}
          </div>
        </form>
      </div>

      <div className="a-table-wrap">
        <div className="a-table-header"><h3>All Users</h3></div>
        {loading ? (
          <div className="a-loader"><div className="a-spinner" /></div>
        ) : (
          <table className="a-table">
            <thead>
              <tr><th>Username</th><th>Role</th><th>Allowed Languages</th><th>Created</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {users.map(u => {
                const langs = u.allowedLangs ? u.allowedLangs.split(',').filter(Boolean) : [];
                return (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600 }}>{u.username}</td>
                    <td>
                      <span style={{ background: u.role === 'admin' ? 'rgba(212,170,106,.15)' : 'rgba(100,100,100,.15)', color: u.role === 'admin' ? 'var(--a-gold)' : 'var(--a-muted)', borderRadius: 4, padding: '2px 8px', fontSize: 12, textTransform: 'uppercase' }}>
                        {u.role}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--a-muted)' }}>
                      {u.role === 'admin' ? <em>All (admin)</em> : langs.length === 0 ? <em>All languages</em> : langs.map(c => {
                        const l = LANG_LIST.find(x => x.code === c);
                        return <span key={c} style={{ marginInlineEnd: 6 }}>{l ? l.label : c}</span>;
                      })}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--a-muted)' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="a-btn-row">
                        <button className="a-icon-btn" onClick={() => startEdit(u)} title="Edit"><i className="fas fa-edit" /></button>
                        <button className="a-icon-btn danger" onClick={() => del(u.id)} title="Delete"><i className="fas fa-trash" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--a-muted)', padding: 32 }}>No users yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
